import { useState } from "react";
import { toast } from "sonner";
import { CalendarX2, FileText, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { ChoixListe } from "@/pages/parametres/ChoixListe";
import { Bloc, Vide } from "./commun";

const aujourdhui = () => new Date().toLocaleDateString("sv-SE");

export const Planning = ({ liste, agents, onMaj }) => {
  const [f, setF] = useState({ user_id: "", type: "absence", date: aujourdhui(), minutes: "", note: "" });
  const ajouter = async (e) => {
    e.preventDefault();
    try { await api.post("/planning", { ...f, minutes: f.minutes ? Number(f.minutes) : null }); toast.success("Ajouté au planning."); setF({ ...f, note: "", minutes: "" }); onMaj(); }
    catch (x) { toast.error(messageErreur(x)); }
  };
  const retirer = async (id) => { try { await api.delete(`/planning/${id}`); onMaj(); } catch (x) { toast.error(messageErreur(x)); } };
  return (
    <Bloc titre="Planning de l'équipe" sous="Absences et retards" testId="team-planning">
      <div className="space-y-1.5 mb-4">
        {liste.length === 0 && <Vide texte="Aucune absence ni retard." testId="planning-empty" />}
        {liste.map((p) => (
          <div key={p.id} className="at-soft p-2.5 flex items-center gap-2 text-sm" data-testid={`planning-row-${p.id}`}>
            <CalendarX2 size={15} className="shrink-0" />
            <span className="flex-1 min-w-0 break-words"><b>{p.date.split("-").reverse().join("/")}</b> · {p.agent} · {p.type === "absence" ? "Absence" : `Retard ${p.minutes} min`}{p.note ? ` · ${p.note}` : ""}</span>
            <button type="button" onClick={() => retirer(p.id)} className="w-11 h-11 shrink-0 flex items-center justify-center" aria-label="Retirer" data-testid={`planning-delete-${p.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <form onSubmit={ajouter} className="grid grid-cols-2 gap-2" data-testid="planning-form">
        <div className="col-span-2"><ChoixListe valeur={f.user_id} onChange={(v) => setF({ ...f, user_id: v })} options={agents.map((a) => ({ code: a.agent_id, label: a.agent }))} placeholder="Agent" testId="planning-agent-select" /></div>
        <ChoixListe valeur={f.type} onChange={(v) => setF({ ...f, type: v })} options={[{ code: "absence", label: "Absence" }, { code: "retard", label: "Retard" }]} testId="planning-type-select" />
        <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="at-input min-w-0" data-testid="planning-date-input" />
        {f.type === "retard" && <input type="number" min="1" value={f.minutes} onChange={(e) => setF({ ...f, minutes: e.target.value })} placeholder="Minutes" className="at-input min-w-0" data-testid="planning-minutes-input" />}
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (facultatif)" className={`at-input min-w-0 ${f.type === "retard" ? "" : "col-span-2"}`} data-testid="planning-note-input" />
        <button type="submit" disabled={!f.user_id} className="at-btn at-btn-primary col-span-2" data-testid="planning-submit"><Plus size={16} />Ajouter</button>
      </form>
    </Bloc>
  );
};

export const FormChallenge = ({ onMaj }) => {
  const [f, setF] = useState({ titre: "", indicateur: "opportunites", objectif: "", debut: aujourdhui(), fin: aujourdhui(), recompense: "" });
  const creer = async (e) => {
    e.preventDefault();
    try { await api.post("/challenges", { ...f, objectif: Number(f.objectif) }); toast.success("Challenge lancé."); setF({ ...f, titre: "", objectif: "", recompense: "" }); onMaj(); }
    catch (x) { toast.error(messageErreur(x)); }
  };
  return (
    <form onSubmit={creer} className="grid grid-cols-2 gap-2" data-testid="challenge-form">
      <input value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} placeholder="Nouveau challenge" className="at-input col-span-2" data-testid="challenge-title-input" />
      <ChoixListe valeur={f.indicateur} onChange={(v) => setF({ ...f, indicateur: v })} testId="challenge-indicator-select"
        options={[{ code: "opportunites", label: "Opportunités" }, { code: "appels", label: "Appels" }, { code: "decroches", label: "Décrochés" }]} />
      <input type="number" min="1" value={f.objectif} onChange={(e) => setF({ ...f, objectif: e.target.value })} placeholder="Objectif" className="at-input min-w-0" data-testid="challenge-goal-input" />
      <input type="date" value={f.debut} onChange={(e) => setF({ ...f, debut: e.target.value })} className="at-input min-w-0" data-testid="challenge-start-input" />
      <input type="date" value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} className="at-input min-w-0" data-testid="challenge-end-input" />
      <input value={f.recompense} onChange={(e) => setF({ ...f, recompense: e.target.value })} placeholder="Récompense (facultatif)" className="at-input col-span-2" data-testid="challenge-reward-input" />
      <button type="submit" className="at-btn at-btn-primary col-span-2" data-testid="challenge-submit"><Plus size={16} />Lancer le challenge</button>
    </form>
  );
};

export const RapportJour = ({ r, onMaj }) => {
  const [occ, setOcc] = useState(false);
  const generer = async () => {
    setOcc(true);
    try { await api.post("/rapport-jour"); toast.success("Rapport généré."); onMaj(); } catch (x) { toast.error(messageErreur(x)); } finally { setOcc(false); }
  };
  return (
    <Bloc titre="Rapport de fin de journée" sous="Généré chaque soir à 18 h" testId="daily-report"
      action={<button type="button" onClick={generer} disabled={occ} className="at-btn at-btn-ghost shrink-0 !px-3 text-sm" data-testid="daily-report-generate"><RefreshCw size={15} className={occ ? "animate-spin" : ""} />Générer maintenant</button>}>
      {!r ? <Vide texte="Aucun rapport pour le moment." testId="daily-report-empty" /> : (
        <div className="space-y-2 text-sm">
          <p className="flex gap-2" data-testid="daily-report-text"><FileText size={15} className="shrink-0 mt-0.5" />{r.texte}</p>
          {r.contenu.top.length > 0 && <p className="text-xs">Podium : {r.contenu.top.map((t, i) => `${i + 1}. ${t.agent} (${t.opportunites})`).join(" · ")}</p>}
          <p className="text-xs at-muted">{r.contenu.absents} absent(s) · {r.contenu.alertes} agent(s) en baisse · {formatDateHeure(r.genere_le)} · {r.par}</p>
          <p className="text-xs at-muted">{r.whatsapp}</p>
        </div>
      )}
    </Bloc>
  );
};
